<!-- File: /client/src/components/webgl/ImageSmear.vue -->
<template>
  <div
    ref="container"
    class="w-full h-full"
  />
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps } from 'vue';
import * as THREE from 'three';
import vertexShader from '!raw-loader!@/assets/shaders/smear.vert'
import fragmentShader from '!raw-loader!@/assets/shaders/smear.frag'

import { setupMouseTracking } from '@/composables/useMouseInteraction';

const props = defineProps({
  imageSrc: { type: String, required: true }
});

const container = ref(null);
let scene, camera, renderer, material, mesh, mouseUniform;

onMounted(() => {
  const width = container.value.clientWidth;
  const height = container.value.clientHeight;

  scene = new THREE.Scene();

  camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  renderer = new THREE.WebGLRenderer({ alpha: true });
  renderer.setSize(width, height);
  container.value.appendChild(renderer.domElement);

  const loader = new THREE.TextureLoader();
  const texture = loader.load(props.imageSrc, () => renderer.render(scene, camera));

  mouseUniform = { value: new THREE.Vector2(0.0, 0.0) };
  const smearStrengthUniform = { value: 0.0 };

  material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uTexture: { value: texture },
      uMouse: mouseUniform,
      uSmearStrength: smearStrengthUniform,
      uResolution: { value: new THREE.Vector2(width, height) },
      uTime: { value: 0.0 }
    }
  });

  const geometry = new THREE.PlaneGeometry(2, 2);
  mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  setupMouseTracking(container.value, (swipeVector, mouseVec2) => {
    mouseUniform.value.copy(mouseVec2);
    smearStrengthUniform.value = swipeVector.length();
  }, {
    minSwipeDistance: 0.002,
    passMouseVec: true
  });

  animate();
});

function animate() {
  requestAnimationFrame(animate);
  material.uniforms.uTime.value += 0.016;

  // Gradually reduce smear effect over time
  material.uniforms.uSmearStrength.value *= 0.95;

  renderer.render(scene, camera);
}

onUnmounted(() => {
  renderer.dispose();
  scene.remove(mesh);
});
</script>

<style scoped>
.container {
  overflow: hidden;
}
</style>
